/**
 * Azure Blob Storage Credential Form
 *
 * A form for configuring Azure Blob Storage credentials with:
 * - Storage Account Key authentication
 * - Connection String authentication
 * - SAS Token authentication
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface AzureBlobStorageCredentialFormProps {
  value: any;
  onChange: (value: any) => void;
  errors?: Record<string, string>;
}

export const AzureBlobStorageCredentialForm: React.FC<AzureBlobStorageCredentialFormProps> = ({
  value,
  onChange,
  errors = {}
}) => {
  const handleChange = (field: string, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue
    });
  };

  const authMethod = value.authMethod || 'storageKey';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="authMethod">Authentication Method</Label>
        <Select
          value={authMethod}
          onValueChange={(val) => handleChange('authMethod', val)}
        >
          <SelectTrigger id="authMethod">
            <SelectValue placeholder="Select authentication method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="storageKey">Storage Account Key</SelectItem>
            <SelectItem value="connectionString">Connection String</SelectItem>
            <SelectItem value="sasToken">SAS Token</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Storage Account Key Authentication */}
      {authMethod === 'storageKey' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="accountName">
              Storage Account Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="accountName"
              type="text"
              value={value.accountName || ''}
              onChange={(e) => handleChange('accountName', e.target.value)}
              placeholder="mystorageaccount"
              className="font-mono text-sm"
            />
            {errors?.accountName && (
              <p className="text-sm text-red-500">{errors.accountName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountKey">
              Account Key <span className="text-red-500">*</span>
            </Label>
            <Input
              id="accountKey"
              type="password"
              value={value.accountKey || ''}
              onChange={(e) => handleChange('accountKey', e.target.value)}
              placeholder="Enter account key"
              className="font-mono text-sm"
            />
            {errors?.accountKey && (
              <p className="text-sm text-red-500">{errors.accountKey}</p>
            )}
          </div>
        </>
      )}

      {/* Connection String Authentication */}
      {authMethod === 'connectionString' && (
        <div className="space-y-2">
          <Label htmlFor="connectionString">
            Connection String <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="connectionString"
            value={value.connectionString || ''}
            onChange={(e) => handleChange('connectionString', e.target.value)}
            placeholder="DefaultEndpointsProtocol=https;AccountName=..."
            rows={4}
            className="font-mono text-xs"
          />
          {errors?.connectionString && (
            <p className="text-sm text-red-500">{errors.connectionString}</p>
          )}
        </div>
      )}

      {/* SAS Token Authentication */}
      {authMethod === 'sasToken' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="accountName">
              Storage Account Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="accountName"
              type="text"
              value={value.accountName || ''}
              onChange={(e) => handleChange('accountName', e.target.value)}
              placeholder="mystorageaccount"
              className="font-mono text-sm"
            />
            {errors?.accountName && (
              <p className="text-sm text-red-500">{errors.accountName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sasToken">
              SAS Token <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="sasToken"
              value={value.sasToken || ''}
              onChange={(e) => handleChange('sasToken', e.target.value)}
              placeholder="sv=2022-11-02&ss=b&srt=sco&sp=rwdlacupiytfx..."
              rows={3}
              className="font-mono text-xs"
            />
            {errors?.sasToken && (
              <p className="text-sm text-red-500">{errors.sasToken}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
